// covered-by: frontends/carrie/src/ask-v2/conversation/__tests__/AskV2ProjectPrivateChats.test.tsx
import { type GetAskPunttProjectResponse } from "@mg/schemas/src/christo/askPunttProject";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CampaignChatDetailHeader } from "../../components/ask-puntt/project-private/CampaignChatDetailHeader";
import { CampaignChatList } from "../../components/ask-puntt/project-private/CampaignChatList";
import { CampaignChatPrivacyBanner } from "../../components/ask-puntt/project-private/CampaignChatPrivacyBanner";
import { CampaignNewChatDraft } from "../../components/ask-puntt/project-private/CampaignNewChatDraft";
import { useAnalytics } from "../../utils/analytics";
import { buildMentionComposerMembers } from "../../utils/buildMentionComposerMembers";
import {
  useCreateAskPunttChat,
  useMarkAskPunttChatRead,
} from "../../utils/queries/askPuntt";
import {
  askPunttProjectKeys,
  useProjectReviews,
  useShareProjectChat,
} from "../../utils/queries/askPunttProjects";
import {
  hasReviewFindings,
  isReviewInPlaceForEmailNotification,
} from "../rail/collectRailContent";
import { AskV2ThreadComposerStack } from "../transport/AskV2ThreadComposerStack";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Interactivity } from "../transport/useAskV2Interactivity";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type ProjectChat = GetAskPunttProjectResponse["chats"][number];
type ProjectMember = NonNullable<
  GetAskPunttProjectResponse["project"]["members"]
>[number];

/** Sentinel for the inline new private chat draft (no persisted chat yet). */
export const ASK_V2_NEW_PRIVATE_CHAT_DRAFT_ID = "__new__";

type AskV2ProjectPrivateChatsProps = {
  projectId: string;
  chats: ProjectChat[];
  sharedChatId: string | null;
  members: ProjectMember[];
  currentUserId: string | undefined;
};

export function AskV2ProjectPrivateChats({
  projectId,
  chats,
  sharedChatId,
  members,
  currentUserId,
}: AskV2ProjectPrivateChatsProps) {
  const { t } = useTranslation("ui");
  const analytics = useAnalytics("ask_v2_project_private_chats");
  const queryClient = useQueryClient();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const createChat = useCreateAskPunttChat();
  const shareChat = useShareProjectChat(projectId);

  const myChats = useMemo(
    () =>
      chats.filter(
        (chat) =>
          chat._id !== sharedChatId &&
          (!currentUserId || chat.userId === currentUserId),
      ),
    [chats, currentUserId, sharedChatId],
  );
  const activeChat =
    activeChatId != null && activeChatId !== ASK_V2_NEW_PRIVATE_CHAT_DRAFT_ID
      ? (myChats.find((chat) => chat._id === activeChatId) ?? null)
      : null;
  const isDraftChat = activeChatId === ASK_V2_NEW_PRIVATE_CHAT_DRAFT_ID;
  const isPrivateChatDetail =
    isDraftChat || activeChat?.projectChatKind === "personal";

  const persistedPrivateChatId =
    activeChatId != null && !isDraftChat ? activeChatId : null;
  const privateTransport = useAskV2Transport({
    chatId: persistedPrivateChatId,
    enabled: persistedPrivateChatId != null,
    presentation: "chat",
  });
  // Third read-only site, same derivation as AskV2ChatPage / AskV2ProjectPage:
  // take it from useAskV2Interactivity rather than hand-rolling the
  // `?? true` + gateTransportWrites pair, so the fail-open rule has one
  // place to change instead of three.
  const { canInteract, gatedTransport: gatedPrivateTransport } =
    useAskV2Interactivity(privateTransport);
  const reviewsQuery = useProjectReviews(projectId);
  // why: getReviewProgressMode is running for any pending warhol turn; the email CTA waits until a review is kicked off
  const privateReviewInProgress = isReviewInPlaceForEmailNotification({
    chat: privateTransport.historyChat,
    messages: privateTransport.historyMessages,
    reviews: reviewsQuery.data,
  });
  const markChatRead = useMarkAskPunttChatRead(persistedPrivateChatId);
  const markChatReadMutate = markChatRead.mutate;
  const tailMessage = privateTransport.historyMessages.at(-1);
  const tailMessageId = tailMessage?._id ?? null;
  const privateHistoryChat = privateTransport.historyChat;
  // why: V2 never sent a read receipt, so the sidebar unread dot and the home
  // @mention task for a chat outlived the reader opening it. One receipt per
  // settled tail; none while this reader's own turn is still posting or the
  // assistant tail is still pending, because completion bumps lastMessageAt
  // server-side and would flip the chat back to unread.
  //
  // Not transport.inFlight: deriveInFlight() calls any user-role tail
  // outstanding (warhol has no pending assistant row), so a chat whose last
  // persisted message is a user turn -- an aborted or errored generation, or an
  // @mention nobody answered -- reads as permanently in flight and never got a
  // receipt at all, leaving it unread however often the reader opened it.
  // Behavior change: such a chat now earns its receipt once this reader's own
  // turn has settled, so it stops showing as unread on every later open.
  const replyOutstanding =
    privateTransport.sending ||
    (tailMessage?.role === "assistant" && tailMessage.status === "pending");
  const tailReadReceiptKey = privateHistoryChat
    ? `${tailMessageId ?? "chat"}:${privateHistoryChat.lastMessageAt ?? ""}`
    : null;
  const lastMarkedReadReceiptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tailReadReceiptKey || replyOutstanding) return;
    // why: a receipt is only honest once the reader has actually LOOKED at the
    // chat, so this is the same document.visibilityState gate AskV2ChatPage
    // carries, for the same reason stated above — markChatRead also runs
    // clearMentionNotificationsForChat, so a receipt sent while the reader is
    // not looking destroys the home task and the @mention behind them.
    // Narrower harm window here than on the chat page: activeChatId only ever
    // comes from a visible click and useAskPunttChatHistory sets no
    // refetchIntervalInBackground, so the poll pauses while hidden. What still
    // reaches this effect is a tail that SETTLES while the tab is backgrounded
    // (the reader opens a chat mid-generation and switches away) — hold that
    // write and send it on the visibilitychange that brings them back, so the
    // two v2 receipt sites cannot drift.
    const markWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (lastMarkedReadReceiptRef.current === tailReadReceiptKey) return;
      lastMarkedReadReceiptRef.current = tailReadReceiptKey;
      markChatReadMutate(
        tailMessageId ? { messageId: tailMessageId } : undefined,
        {
          onSettled: () => {
            void queryClient.invalidateQueries({
              queryKey: askPunttProjectKeys.project(projectId),
            });
          },
        },
      );
    };
    markWhenVisible();
    // Re-runs (and so re-binds with a fresh key) whenever the tail settles or
    // advances, which is why the handler can close over the key directly
    // instead of reading it back out of a ref.
    document.addEventListener("visibilitychange", markWhenVisible);
    return () =>
      document.removeEventListener("visibilitychange", markWhenVisible);
  }, [
    markChatReadMutate,
    projectId,
    queryClient,
    replyOutstanding,
    tailMessageId,
    tailReadReceiptKey,
  ]);
  const isChatOwner =
    !!privateTransport.historyChat &&
    !!currentUserId &&
    privateTransport.historyChat.userId === currentUserId &&
    privateTransport.historyChat.role !== "shared";
  const mentionComposerMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        isChatOwner ? members : [],
        privateTransport.historyChat?.allowList ?? [],
      ),
    [isChatOwner, members, privateTransport.historyChat?.allowList],
  );

  const backToList = useCallback(() => {
    setActiveChatId(null);
  }, []);

  const handleCreatePersonalChat = async (
    message: string,
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[],
  ): Promise<boolean> => {
    const msg = message.trim();
    if (!msg && (!attachments || attachments.length === 0)) return false;
    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: "general",
        projectId,
        projectChatKind: "personal",
        attachments: attachments?.length ? attachments : undefined,
      });
      analytics.capture("ask_puntt_chat_created", {
        workflow_type: "general",
        message_length: msg.length,
        has_attachments: (attachments?.length ?? 0) > 0,
        attachment_count: attachments?.length ?? 0,
        attachment_ids: attachments?.map((att) => att.key) ?? [],
        project_id: projectId,
        project_chat_kind: "personal",
        is_new_chat: true,
      });
      analytics.capture("ask_puntt_campaign_personal_chat_created", {
        projectId,
      });
      await queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      setActiveChatId(result.chat._id);
      return true;
    } catch (error) {
      console.error("Failed to create personal chat:", error);
      return false;
    }
  };

  if (activeChatId == null) {
    return (
      <CampaignChatList
        chats={myChats}
        currentUserId={currentUserId}
        members={members}
        onSelect={setActiveChatId}
        onNewChat={() => setActiveChatId(ASK_V2_NEW_PRIVATE_CHAT_DRAFT_ID)}
        isCreating={createChat.isPending}
      />
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-project-private-chats"
    >
      <CampaignChatDetailHeader
        title={isDraftChat ? "New chat" : (activeChat?.title ?? "Chat")}
        isShared={activeChat?.projectChatKind === "shared"}
        onBack={backToList}
        onShare={() => {
          if (activeChatId && !isDraftChat) {
            shareChat.mutate(activeChatId);
          }
        }}
        isSharing={shareChat.isPending}
        shareDisabled={isDraftChat}
      />
      {isPrivateChatDetail ? <CampaignChatPrivacyBanner /> : null}
      {isDraftChat ? (
        <CampaignNewChatDraft
          data-testid="ask-v2-project-new-chat-draft"
          onSubmit={handleCreatePersonalChat}
          isSubmitting={createChat.isPending}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 py-6"
            data-testid="ask-v2-project-private-thread-scroll"
          >
            <div className="mx-auto w-full max-w-[720px] space-y-4">
              {privateTransport.historyLoading &&
              privateTransport.messages.length === 0 ? (
                <AskV2ThreadLoading />
              ) : privateTransport.historyError &&
                privateTransport.messages.length === 0 ? (
                <p
                  className="py-8 text-center text-sm text-puntt-neutral-gray-10"
                  data-testid="ask-v2-project-private-thread-error"
                >
                  {t("askV2.pages.chat.loadError")}
                </p>
              ) : (
                <AskV2Thread
                  transport={gatedPrivateTransport}
                  presentation="chat"
                  currentUserId={currentUserId}
                  readOnly={!canInteract}
                />
              )}
            </div>
          </div>
          <div
            className="shrink-0 border-t border-puntt-neutral-gray-6 p-4"
            data-testid="ask-v2-project-private-composer"
          >
            <div className="mx-auto w-full max-w-[720px]">
              <AskV2ThreadComposerStack
                chatId={persistedPrivateChatId}
                reviewInProgress={privateReviewInProgress}
              >
                <AskV2SummonBar
                  transport={gatedPrivateTransport}
                  mentionMembers={mentionComposerMembers}
                  rosterMembers={isChatOwner ? members : []}
                  enableDirectoryMentions={isChatOwner}
                  composerAriaLabel={t("replyToChat")}
                  readOnly={!canInteract}
                  hasFindings={hasReviewFindings(reviewsQuery.data)}
                />
              </AskV2ThreadComposerStack>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
